import { Link } from 'react-router-dom';
import { Box, Stack } from '@mui/material';

import { Button, EditIcon } from '@occc/ui-design-system';
import { Page } from '@occc-app/design-system';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { UpdateRetailerNetworkLocations } from '@occc-app/App/PolicyBasedAccessControl/userActions';
import {
  RetailerNetworkLocationDataFragment,
  RetailerNetworkLocationFuelPricesFragment,
} from '@occc/graphql';

import { GeneralDetailsContainer } from './components/general-details.container';
import { FuelPricesCard } from './components/fuel-prices-card.component';
import { StoreBrandingCard } from './components/store-branding-card.component';
import { StoreConfigurationCard } from './components/store-configuration-card.component';
import { StoreLocationMapCard } from './components/store-location-map-card.component';
import { useStoreOfflineBanner } from './use-store-offline-banner.hook';
import { ConnectionStatusContainer } from './components/connection-status.container';

import { LocationUsersTableContainer } from '../retailer-network-location-details-page/components/location-users-table/location-users-table.container';
import { useLocationSchedule } from '../retailer-network-location-details-page/components/location-schedule-card';
import { useLocationAttributes } from '../retailer-network-location-details-page/components/location-attributes-card';
import { useRetailerNetworkUserSelector } from '../retailer-network-context/use-retailer-network-user.selector';

const PAGE_TITLE = 'Store Details';
const USERS_TABLE_TITLE = 'Store Admins and Users';

type RetailerNetworkMyStoresStoreV2PageProps = {
  location: RetailerNetworkLocationDataFragment & RetailerNetworkLocationFuelPricesFragment;
  networkUuid: string;
  editDetailsTo: string;
};

export const RetailerNetworkMyStoresStoreV2Page = ({
  location,
  networkUuid,
  editDetailsTo,
}: RetailerNetworkMyStoresStoreV2PageProps) => {
  const locationName = location.name || location.siteSystemConfiguration?.[0]?.identifier || '';
  const storeId = location.siteSystemConfiguration?.[0]?.identifier;
  const { isRetailerNetworkSiteUser } = useRetailerNetworkUserSelector();
  const { data: scheduleData } = useLocationSchedule({ location });
  const { data: attributesData } = useLocationAttributes({ location });
  useStoreOfflineBanner(location.siteSystemConfiguration);

  return (
    <Page
      browserTabTitle={locationName || PAGE_TITLE}
      pageHeaderProps={{
        title: locationName || PAGE_TITLE,
        titleTestId: 'rc-store-v2-title',
        subtitle: storeId ? `Store ID: ${storeId}` : undefined,
      }}
      containerProps={{ variant: 'md' }}
      actions={
        <Can I={UpdateRetailerNetworkLocations({ uuid: networkUuid })}>
          <Button
            variant='contained'
            color='secondary'
            startIcon={<EditIcon />}
            component={Link}
            to={editDetailsTo}
            role='button'
          >
            Edit Details
          </Button>
        </Can>
      }
    >
      <Stack spacing={1}>
        <GeneralDetailsContainer
          retailerNetworkUuid={networkUuid}
          locationUuid={location.uuid}
          address={location.address}
          phoneNumbers={location.phoneNumbers.map(({ phoneNumber }) => phoneNumber)}
        />

        <FuelPricesCard fuelGradePrices={location.fuelGradePrices} />

        <ConnectionStatusContainer retailerNetworkUuid={networkUuid} locationUuid={location.uuid} />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems='stretch'>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StoreConfigurationCard
              schedule={scheduleData.scheduleList}
              attributes={attributesData.attributes}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StoreLocationMapCard address={location.address} />
          </Box>
        </Stack>

        <StoreBrandingCard brandingDetails={location.brandingDetails} />

        {!isRetailerNetworkSiteUser && (
          <LocationUsersTableContainer
            retailerNetworkUuid={networkUuid}
            location={location}
            title={USERS_TABLE_TITLE}
            searchParamName='users-search'
            beforeParamName='users-before'
            afterParamName='users-after'
            userRoleFilterParamName='users-user-role-filter'
          />
        )}
      </Stack>
    </Page>
  );
};
